/**
 * based on this solution - https://github.com/bennadel/JavaScript-Demos/blob/master/demos/window-edge-scrolling/index.htm
 */

import UIConstants from '../constants/UIConstants';
import utilService from './UtilService';

const EDGE_SIZE = 50;
const MAX_STEP = 20;

let adjustableElement;
let timerId = null;
let viewportX;
let viewportY;
let viewportWidth;
let viewportHeight;
let clientRect;
let edgeTop;
let edgeLeft;
let edgeBottom;
let edgeRight;
let useVerticalScroll;
let useHorizontalScroll;
let maxScrollX;
let maxScrollY;
let isInLeftEdge;
let isInRightEdge;
let isInTopEdge;
let isInBottomEdge;
let listenersAdded = false;

const handleDragMouseMove = (event) => {
    adjustableElement = getadjustableElement(event.target);
    if (!adjustableElement) {
        // do nothing if cursor is out of adjustable element
        return;
    }

    defineViewportSizes(event);
    defineEdgeBorders();
    defineInEdgeFlags();

    if (!isOverEdge()) {
        // do nothing if cursor is not over the scroll edge
        clearTimeout(timerId);
        return;
    }

    defineMaxScrolls();
    checkForWindowScroll(adjustableElement);
};

const defineViewportSizes = (event) => {
    // Get the viewport-relative coordinates of the mousemove event.
    viewportX = event.clientX;
    viewportY = event.clientY;

    // Get the viewport dimensions.
    viewportWidth = adjustableElement.clientWidth;
    viewportHeight = adjustableElement.clientHeight;

    clientRect = adjustableElement.getBoundingClientRect();
};

const defineEdgeBorders = () => {
    edgeTop = clientRect.top + EDGE_SIZE;
    edgeLeft = clientRect.left + EDGE_SIZE;
    edgeBottom = clientRect.bottom - EDGE_SIZE;
    edgeRight = clientRect.right - EDGE_SIZE;
};

const defineMaxScrolls = () => {
    const targetWidth = Math.max(
        adjustableElement.scrollWidth,
        adjustableElement.offsetWidth,
        adjustableElement.clientWidth
    );
    const targetHeight = Math.max(
        adjustableElement.scrollHeight,
        adjustableElement.offsetHeight,
        adjustableElement.clientHeight
    );

    maxScrollX = targetWidth - viewportWidth;
    maxScrollY = targetHeight - viewportHeight;
};

const defineInEdgeFlags = () => {
    const isInsideY = viewportY > clientRect.top && viewportY < clientRect.bottom;
    const isInsideX = viewportX > clientRect.left && viewportX < clientRect.right;

    isInLeftEdge = viewportX > clientRect.left && viewportX < edgeLeft && isInsideY;
    isInRightEdge = viewportX < clientRect.right && viewportX > edgeRight && isInsideY;
    isInTopEdge = viewportY > clientRect.top && viewportY < edgeTop && isInsideX;
    isInBottomEdge = viewportY < clientRect.bottom && viewportY > edgeBottom && isInsideX;
};

const getadjustableElement = (element) => {
    return element.classList.contains(UIConstants.GAP_MATCH_SCROLL_CLASS) ? element : element.closest(`.${UIConstants.GAP_MATCH_SCROLL_CLASS}`);
};

const checkForWindowScroll = (target) => {
    clearTimeout(timerId);

    // repeat while scrolling is needed
    if (doVerticalScroll(target) || doHorizonalScroll(target)) {
        timerId = setTimeout(checkForWindowScroll.bind(this, target), 30);
    }
};

const doVerticalScroll = (target) => {
    if (!useVerticalScroll) {
        return false;
    }

    const currentScrollY = target.scrollTop;
    const canScrollUp = currentScrollY > 0;
    const canScrollDown = currentScrollY < maxScrollY;

    let nextScrollY = currentScrollY;

    if (isInTopEdge && canScrollUp) {
        const intensity = (edgeTop - viewportY) / EDGE_SIZE;
        nextScrollY -= (MAX_STEP * intensity);
    } else if (isInBottomEdge && canScrollDown) {
        const intensity = (viewportY - edgeBottom) / EDGE_SIZE;
        nextScrollY += (MAX_STEP * intensity);
    } else {
        return false;
    }

    nextScrollY = Math.max(0, Math.min(maxScrollY, nextScrollY));

    if (nextScrollY !== currentScrollY) {
        target.scrollTop = nextScrollY;
        return true;
    }
    return false;
};

const doHorizonalScroll = (target) => {
    if (!useHorizontalScroll) {
        return false;
    }

    const currentScrollX = target.scrollLeft;
    const canScrollLeft = currentScrollX > 0;
    const canScrollRight = currentScrollX < maxScrollX;
    let nextScrollX = currentScrollX;

    if (isInLeftEdge && canScrollLeft) {
        const intensity = (edgeLeft - viewportX) / EDGE_SIZE;
        nextScrollX -= (MAX_STEP * intensity);
    } else if (isInRightEdge && canScrollRight) {
        const intensity = (viewportX - edgeRight) / EDGE_SIZE;
        nextScrollX += (MAX_STEP * intensity);
    }

    nextScrollX = Math.max(0, Math.min(maxScrollX, nextScrollX));

    if (nextScrollX !== currentScrollX) {
        target.scrollLeft = nextScrollX;
        return true;
    }
    return false;
};

const isOverEdge = () => {
    return isInLeftEdge || isInRightEdge || isInTopEdge || isInBottomEdge;
};

const handleDragMouseLeave = () => {
    clearTimeout(timerId);
};

const dragAdjustmentService = {
    init: (vertical = true, horizontal = false) => {
        // do not adjust on mobile devices
        if (utilService.isMobileDevice() || listenersAdded) {
            return;
        }
        useVerticalScroll = vertical;
        useHorizontalScroll = horizontal;

        // do not add event listener if both params are false or there are no scroll adjustment elements
        const root = document.getElementsByClassName(UIConstants.GAP_MATCH_SCROLL_CLASS);
        if (root && (useVerticalScroll || useHorizontalScroll)) {
            listenersAdded = true;
            window.addEventListener('dragover', handleDragMouseMove, false);
            window.addEventListener('dragleave', handleDragMouseLeave, false);
        }
    },

    destroy: () => {
        if (!utilService.isMobileDevice() && listenersAdded) {
            window.removeEventListener('dragover', handleDragMouseMove);
            window.removeEventListener('dragleave', handleDragMouseLeave);
            listenersAdded = false;
        }
    },
};

export default dragAdjustmentService;
